// [2026-08-12] ADDED — the InC-Proctor dashboard's API client.
//
// Used for exactly one call: exchanging this operator's live InCruiter admin session for a
// single-use handoff code, so clicking InC-Proctor in the sidebar opens the proctoring dashboard
// already signed in. The common admin login is the only login — there is no separate account.
//
// Shaped after utils/api/interview.jsx, which is the house pattern for one axios instance per
// backend service.

import axios from "axios";
// [2026-08-12] CHANGED — `removeToken` is deliberately NOT imported. See the response interceptor
// below for why a 401 from this service must not sign the operator out of the admin.
import { generateUrl, getToken } from "../helper";
// [2026-08-12] CHANGED — the redux store is deliberately NOT imported here.
//
// OLD:
//   import { validateLogin } from "../../redux/action";
//   import { store } from "../../redux/store";
//   ... store.dispatch(validateLogin(true)) in the success interceptor
//
// WHY IT WENT: copied from interview.jsx, where it marks the admin session as still valid. This
// client makes exactly one call — a sign-in handoff — and has no need to refresh that flag; the
// instances that own the session already do it on every request.
//
// It also cost something real. utils/helper.js:11 imports the store, whose saga tree imports
// helper back, so importing this module pulled a cycle in that resolves to
// `generateUrl is not a function` whenever helper is the entry point. That is a PRE-EXISTING cycle
// in this repo and is not fixed here — but there is no reason for a two-call API client to sit
// inside it.

const proctoring = axios.create({
  // [2026-09-07] CHANGED — this points at the STAFF CONSOLE host now, and it carries credentials.
  //
  // OLD:
  //   // generateUrl("incproctor") returns the dashboard ROOT. That one host serves both the API
  //   // (under /admin/v1) and the dashboard UI (at /), so a single setting covers the handoff
  //   // call and the redirect that follows it.
  //   baseURL: `${generateUrl("incproctor")}/admin/v1`,
  //
  // WHY THE HOST HAD TO MOVE. `incproctor` is the CUSTOMER dashboard host; `incproctor_admin` is
  // the STAFF console — and the staff console is where ProctoringLaunch sends the operator. The
  // sign-in below works by having the proctoring service SET A SESSION COOKIE. A cookie carrying
  // no Domain attribute is host-only, so one set by the customer host would simply not exist on
  // the staff console: the operator would be signed in somewhere they were not going, and would
  // still meet a login form where they were.
  //
  // [2026-09-07] CHANGED AGAIN — to the API host. THE PARAGRAPH ABOVE WAS WRONG.
  //
  // OLD: baseURL: `${generateUrl("incproctor_admin")}/admin/v1`,
  //
  // It reasoned that the call and the redirect must share an origin so the session cookie lands on
  // the console. The premise — that the console host serves the API — was never checked, and it is
  // false. `incproctor_admin` is a static nginx serving the Vite bundle; POST and OPTIONS answer 405
  // there, so the preflight died and the browser said "No 'Access-Control-Allow-Origin' header is
  // present". Correct, and misleading: there was no API to send one.
  //
  // THE COOKIE STILL WORKS, and this is why the shared-origin invariant was never needed. The
  // session cookie is set for the API HOST, and the console talks to that same API host for
  // everything it does — verified 2026-09-07, the deployed console's bundle calls
  // https://dev.v1.proctoring-sdk-backend.incruiter.com, and a preflight from the console's origin
  // to that host answers 204. So the cookie is set on exactly the host the console will present it
  // to. SameSite=Lax permits it because every host here sits under incruiter.com, which makes them
  // SAME-SITE even though all three are different origins.
  //
  // THREE HOSTS, NOT TWO — this is the shape that has to be held in mind:
  //   incproctor        customer dashboard  (static)
  //   incproctor_admin  staff console       (static)   <- the redirect target
  //   incproctor_api    the FastAPI service            <- every request goes here
  baseURL: `${generateUrl("incproctor_api")}/admin/v1`,
  // REQUIRED, or the sign-in means nothing. Without it the browser discards the Set-Cookie on a
  // cross-origin response, the call still returns 200, and the redirect lands on a login screen —
  // the most confusing possible outcome, because nothing reports an error.
  //
  // Safe here because the proctoring service answers credentialed CORS only for origins listed
  // explicitly in GATEWAY_ADMIN_ALLOWED_ORIGINS, never a wildcard
  // (proctoring-sdk-backend/app/middleware/admin_cors.py).
  withCredentials: true,
});
proctoring.interceptors.request.use(
  function (config) {
    let token = getToken();
    if (token && token !== null) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // NOTE: encryptPasswordFields is deliberately NOT applied. This client never sends a password
    // field, and running the helper over a body that has none only creates a way for it to mangle
    // something later.
    return config;
  },
  function (error) {
    return Promise.reject(error);
  },
);
proctoring.interceptors.response.use(
  (response) => {
    // No store.dispatch here — see the import block at the top for why.
    return response;
  },
  function (error) {
    // [2026-08-12] CHANGED — a 401 from this service must NOT sign the operator out of the admin.
    //
    // OLD:
    //   if (error.response?.status === 401) {
    //     removeToken();
    //     window.location.href = "/login";
    //     return Promise.reject(error);
    //   }
    //
    // WHY IT HAD TO CHANGE: that is the right response when USER-AUTH rejects a token — the admin
    // session really is dead. It is wrong here. A 401 from the proctoring service means only "this
    // service does not recognise your session"; the admin session behind it is perfectly valid.
    // Copying the sibling instances verbatim meant one click on InC-Proctor could throw away a
    // working admin login. The customer-side twin did exactly that on 2026-08-12.
    //
    // A failure in an optional product must never damage the session of the product that launched
    // it. The error goes to the saga, which shows a message and leaves the operator where they are.
    return Promise.reject(error);
  },
);
export default proctoring;
